/**
 * How long is left on a Ready Check (2026-09-08).
 *
 * The server sends both an absolute `deadline` (its own wall clock) and the
 * `timeoutMs` the check was opened with. Counting down against `deadline`
 * means counting the *difference between two machines' clocks* as well as the
 * time — a browser running three seconds fast opens the check at "12s", and
 * one running slow shows a number that never reaches zero before the server
 * has already timed the player out. Neither clock is wrong; they were never
 * required to agree.
 *
 * So the duration is what travels, and the expiry is stamped here, on the
 * clock that will later be asked what time it is. What that trades away is
 * the network latency between emit and receipt, which is tens of milliseconds
 * against a fifteen-second window.
 *
 * Pure and separate from the hub because the arithmetic is the part that goes
 * wrong, and it went wrong silently: every number on screen looked plausible.
 */

export interface ReadyCheckTiming {
  /** The server's wall clock. Only used when `timeoutMs` is absent. */
  deadline: number;
  timeoutMs?: number;
}

/**
 * When the check runs out, on the caller's own clock.
 *
 * `nowMs` is passed in rather than read, so a test can hold time still and so
 * the caller cannot accidentally stamp this at render time instead of at
 * receipt — stamping late is the same bug in a smaller coat.
 */
export function readyCheckExpiryAt(timing: ReadyCheckTiming, nowMs: number): number {
  // A server old enough not to send the duration still gets the old
  // arithmetic. It is no worse than what it replaces, and clamped at zero so
  // a badly skewed clock produces an expired check rather than a negative one.
  const remaining = timing.timeoutMs ?? Math.max(0, timing.deadline - nowMs);
  return nowMs + Math.max(0, remaining);
}

/** Whole seconds left, rounded up so the last one is shown as "1s" rather than "0s". */
export function readyCheckSecondsLeft(expiresAtMs: number, nowMs: number): number {
  return Math.max(0, Math.ceil((expiresAtMs - nowMs) / 1000));
}
